import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Home, Layers, LogIn, UserCircle2, ShoppingBag, MapPin, Navigation,
  Shield, Package, Tag, Inbox, Users, Truck, Radio, Settings, Bell,
  Globe, Lock, Wrench, BellRing, Phone, Mail, Map, FileDown, Star,
  ExternalLink, ChevronRight, Smartphone, ArrowDownRight, Sparkles,
  KeyRound, ImageIcon, Type, MessageSquare, Percent, Scissors
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const ROLE_TINT = {
  public: { bg: 'from-sky-500 to-blue-600', soft: 'bg-sky-500/10', ring: 'ring-sky-400/30', dot: 'bg-sky-400', text: 'text-sky-300', solid: 'bg-sky-500' },
  customer: { bg: 'from-violet-500 to-fuchsia-600', soft: 'bg-violet-500/10', ring: 'ring-violet-400/30', dot: 'bg-violet-400', text: 'text-violet-300', solid: 'bg-violet-500' },
  driver: { bg: 'from-emerald-500 to-teal-600', soft: 'bg-emerald-500/10', ring: 'ring-emerald-400/30', dot: 'bg-emerald-400', text: 'text-emerald-300', solid: 'bg-emerald-500' },
  admin: { bg: 'from-amber-500 to-orange-600', soft: 'bg-amber-500/10', ring: 'ring-amber-400/30', dot: 'bg-amber-400', text: 'text-amber-300', solid: 'bg-amber-500' },
  api: { bg: 'from-slate-500 to-slate-700', soft: 'bg-slate-500/10', ring: 'ring-slate-400/30', dot: 'bg-slate-400', text: 'text-slate-300', solid: 'bg-slate-500' },
};

const SECTIONS = [
  {
    id: 'public',
    title: 'Public storefront',
    desc: 'Open to everyone — no sign-in required.',
    icon: Globe,
    color: 'public',
    routes: [
      { path: '/', label: 'Home', icon: Home, desc: 'Hero, category tiles, featured slideshow, brand-promise tiles, B2B inquiries.' },
      { path: '/category/water', label: 'Division', icon: Layers, desc: 'Product grid + per-division custom filters. OOS items keep full colour + show "Notify me when back" capture.', dynamic: '/category/:slug' },
      { path: '/track/MR-XXXXXX-XXXXX', label: 'Order tracking', icon: Navigation, desc: 'Live driver position, status timeline, click-to-call.', dynamic: '/track/:orderNumber' },
      { path: '/login', label: 'Customer sign-in', icon: LogIn, desc: 'OTP via email + WebAuthn Passkey (Face ID / Touch ID).' },
    ],
    features: ['Brand promise tiles (admin editable)', 'Slideshow (TV mode)', 'Trade inquiry capture', 'Back-in-stock email subscribe'],
  },
  {
    id: 'customer',
    title: 'Customer account',
    desc: 'After OTP or Passkey sign-in.',
    icon: UserCircle2,
    color: 'customer',
    routes: [
      { path: '/my-orders', label: 'My orders', icon: ShoppingBag, desc: 'Past orders, PDF invoices, reorder, track active order, push opt-in, order live chat.' },
    ],
    features: ['Cart drawer + GPS capture', 'OTP + Passkey login', 'B2B tier discount auto-applied', 'Manual per-product price overrides', 'Order live chat (voice + text)', 'Push notifications', 'PWA install (maskable icon)'],
  },
  {
    id: 'driver',
    title: 'Driver portal',
    desc: 'Mobile-first dark theme, installable as PWA.',
    icon: Truck,
    color: 'driver',
    routes: [
      { path: '/driver/login', label: 'Sign in', icon: LogIn, desc: 'Phone or email + password, eye toggle.' },
      { path: '/driver/signup', label: 'Apply to drive', icon: Sparkles, desc: 'Self-signup → pending until admin approves.' },
      { path: '/driver', label: 'Dashboard', icon: Truck, desc: 'Assigned orders, GPS ping every 20s, status flow, voice messaging with admin.' },
    ],
    features: ['Live GPS ping', 'One-tap status flow', 'Order live chat (admin)', 'Passkey login', 'Push notifications', 'PWA install + onboarding QR card'],
  },
  {
    id: 'admin',
    title: 'Admin control center',
    desc: 'Email + password OR Passkey (staff only).',
    icon: Shield,
    color: 'admin',
    routes: [
      { path: '/admin/login', label: 'Sign in', icon: Lock, desc: 'Email/password + Face ID / Touch ID Passkey option.' },
      { path: '/admin', label: 'Dashboard', icon: Wrench, desc: 'Revenue, orders, top products, recent activity, broadcast push.' },
      { path: '/admin/orders', label: 'Orders', icon: ShoppingBag, desc: 'Filter, expand, assign driver, change status, download invoice, message customer & driver.' },
      { path: '/admin/dispatch', label: 'Dispatch', icon: Radio, desc: 'Live Leaflet map (streets + satellite), drivers + orders, auto-assign nearest driver (haversine + load), fleet fuel-savings card (km saved this week + leaderboard), broadcast push.' },
      { path: '/admin/drivers', label: 'Drivers', icon: Truck, desc: 'CRUD + approve / reject pending signups + reset password + email QR onboarding card.' },
      { path: '/admin/products', label: 'Products', icon: Package, desc: 'Inline-edit grid, bulk edit/delete, Excel/CSV upload, custom attribute column, AI background removal.' },
      { path: '/admin/categories', label: 'Divisions', icon: Tag, desc: 'CRUD + define custom filters (Brand, Origin, etc.) per division, visibility, ordering.' },
      { path: '/admin/customers', label: 'Customers', icon: Users, desc: 'B2B tier, manual per-product price overrides, customer numbers + QR/barcode cards, passkey controls.' },
      { path: '/admin/inquiries', label: 'Trade inquiries', icon: Inbox, desc: 'B2B leads with click-to-call / WhatsApp / copy, admin-only.' },
      { path: '/admin/credentials', label: 'Credentials', icon: KeyRound, desc: 'Change admin password, manage admin passkeys, SMTP diagnostics.' },
      { path: '/admin/settings', label: 'Site settings', icon: Settings, desc: 'CMS — tabs: Visibility, Branding (brand-promise tiles + logo + footer), Hero, Slideshow, Sections, Footer, Documents (PDF themes), Typography, Favicon.' },
      { path: '/admin/sitemap', label: 'Sitemap', icon: Map, desc: 'You are here — printable architecture overview.' },
    ],
    features: ['Real-time notifications bell', 'CMS — toggle every section', 'AI background removal (fal.ai + local fallback)', 'Customizable PDF invoices/receipts/manifests/cards', 'Dynamic favicon generator', 'Brevo HTTP email API'],
  },
];

const ROLE_API = {
  public: [
    'GET /api/categories', 'GET /api/products', 'GET /api/settings',
    'POST /api/orders', 'GET /api/track/:order_number',
    'POST /api/inquiries',
    'POST /api/products/:id/notify-when-in-stock',
    'POST /api/auth/request-otp', 'POST /api/auth/verify-otp',
    'POST /api/auth/passkey/login-challenge', 'POST /api/auth/passkey/login-verify',
    'POST /api/driver/signup', 'POST /api/driver/login',
    'POST /api/auth/driver/passkey/login-challenge', 'POST /api/auth/driver/passkey/login-verify',
    'POST /api/auth/admin/passkey/login-challenge', 'POST /api/auth/admin/passkey/login-verify',
    'GET /api/push/vapid-public-key', 'GET /api/pricing/tiers',
    'GET /api/images/:image_id',
  ],
  customer: [
    'GET /api/customer/me', 'GET /api/customer/orders',
    'GET /api/customer/notifications', 'POST /api/customer/notifications/mark-read',
    'POST /api/customer/location',
    'GET /api/customer/passkeys',
    'POST /api/auth/passkey/register-challenge', 'POST /api/auth/passkey/register-verify',
    'GET /api/orders/:id/invoice',
    'POST /api/customer/push/subscribe', 'POST /api/customer/push/unsubscribe',
    'GET /api/customers/lookup/:customer_number',
  ],
  driver: [
    'GET /api/driver/me', 'GET /api/driver/orders',
    'PUT /api/driver/orders/:id/status', 'POST /api/driver/location', 'POST /api/driver/heartbeat',
    'POST /api/driver/route-optimization-log',
    'GET /api/driver/orders/:order_id/messages', 'POST /api/driver/orders/:order_id/messages',
    'POST /api/driver/orders/:order_id/messages/voice',
    'POST /api/driver/passkey/register-challenge', 'POST /api/driver/passkey/register-verify',
    'GET /api/driver/messages/unread-count',
    'GET /api/driver/notifications', 'POST /api/driver/push/subscribe',
  ],
  admin: [
    'POST /api/admin/login', 'POST /api/admin/change-password', 'GET /api/admin/me',
    'GET /api/admin/stats', 'GET /api/admin/orders', 'POST /api/admin/orders',
    'POST /api/admin/orders/:id/assign', 'PUT /api/admin/orders/:id/status',
    'GET /api/admin/orders/:id/suggest-driver', 'GET /api/admin/dispatch',
    'GET /api/admin/route-optimization/stats',
    'GET /api/admin/orders/:id/messages', 'POST /api/admin/orders/:id/messages',
    'POST /api/admin/orders/:id/messages/voice', 'POST /api/admin/drivers/broadcast',
    'CRUD /api/admin/products', 'POST /api/admin/products/bulk-edit',
    'POST /api/admin/products/bulk-delete', 'POST /api/admin/products/bulk-upload',
    'GET /api/admin/products/bulk-template',
    'CRUD /api/admin/categories', 'CRUD /api/admin/drivers',
    'GET /api/admin/customers', 'PUT /api/admin/customers/:email',
    'PUT /api/admin/customers/:email/tier',
    'GET|PUT /api/admin/customers/:email/price-overrides',
    'DELETE /api/admin/customers/:email/passkeys/:id',
    'POST /api/admin/customers/:email/email-card',
    'GET /api/admin/inquiries', 'PUT /api/admin/inquiries/:id/status',
    'GET|PUT /api/admin/settings',
    'POST /api/admin/upload', 'POST /api/admin/favicon/regenerate-pngs',
    'GET /api/admin/driver-card-pdf', 'GET /api/admin/pdf-preview/:doc_type',
    'GET /api/admin/pdf-defaults', 'GET /api/admin/card-defaults', 'GET /api/admin/favicon-defaults',
    'GET /api/admin/notifications', 'GET /api/admin/drivers/pending-count',
    'GET /api/admin/smtp-status', 'POST /api/admin/smtp-test',
    'POST /api/admin/passkey/register-challenge', 'POST /api/admin/passkey/register-verify',
  ],
};

const Pill = ({ tint, children }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] ${tint.soft} ${tint.text} ring-1 ${tint.ring}`}>
    {children}
  </span>
);

const SectionHeader = ({ section }) => {
  const tint = ROLE_TINT[section.color];
  const Icon = section.icon;
  return (
    <div className="flex items-start gap-4 mb-6">
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tint.bg} flex items-center justify-center shadow-2xl shadow-black/40 flex-shrink-0`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <Pill tint={tint}>{section.id}</Pill>
        <h2 className="font-display text-2xl sm:text-3xl text-white font-bold leading-tight mt-2">{section.title}</h2>
        <p className="text-slate-400 text-sm mt-1">{section.desc}</p>
      </div>
    </div>
  );
};

const RouteCard = ({ route, tint }) => {
  const Icon = route.icon;
  return (
    <Link
      to={route.path}
      className={`group relative rounded-2xl bg-white/[0.04] hover:bg-white/[0.07] ring-1 ring-white/10 hover:ring-white/20 transition-all duration-300 p-5 flex flex-col gap-3 backdrop-blur block`}
      data-testid={`sitemap-route-${route.path.replace(/[/:]/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`w-10 h-10 rounded-xl ${tint.soft} ring-1 ${tint.ring} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${tint.text}`} />
        </div>
        <ExternalLink className="w-4 h-4 text-slate-500 opacity-0 group-hover:opacity-100 transition" />
      </div>
      <div>
        <div className="font-display text-base font-bold text-white">{route.label}</div>
        <code className={`text-[11px] font-mono ${tint.text} bg-black/30 px-1.5 py-0.5 rounded mt-1 inline-block`}>
          {route.dynamic || route.path}
        </code>
      </div>
      <p className="text-slate-400 text-xs leading-relaxed">{route.desc}</p>
    </Link>
  );
};

const SiteMap = () => {
  const { token } = useAdminAuth();
  const [view, setView] = useState('map'); // map | api

  // Inject print stylesheet only while mounted
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'sitemap-print-css';
    style.textContent = `
      @media print {
        @page { size: A4; margin: 14mm 12mm; }
        body { background: #ffffff !important; }
        .no-print, [data-noprint="true"] { display: none !important; }
        .print-light { background: #ffffff !important; color: #0F172A !important; }
        .print-light * { color: #0F172A !important; }
        .print-light .text-slate-400, .print-light .text-slate-500 { color: #64748B !important; }
        .print-light .bg-white\\/\\[0\\.04\\], .print-light .bg-white\\/5 { background: #F8FAFC !important; }
        .print-light .ring-white\\/10, .print-light .ring-1 { box-shadow: inset 0 0 0 1px #E2E8F0 !important; }
        .print-light h2, .print-light h1, .print-light .font-display { color: #0F172A !important; }
        .print-light section { page-break-inside: avoid; }
        a { text-decoration: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById('sitemap-print-css');
      if (el) el.remove();
    };
  }, []);

  if (!token) return <Navigate to="/admin/login" replace />;

  const handleDownload = () => {
    // Browser print dialog → "Save as PDF"
    window.print();
  };

  return (
    <main className="min-h-screen bg-[#070B16] text-white relative overflow-hidden print-light">
      {/* Grid background */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none no-print"
        style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '32px 32px' }}
      />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-blue-500/10 blur-[160px] pointer-events-none no-print" />
      <div className="absolute top-1/2 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-[160px] pointer-events-none no-print" />

      {/* Header */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin" className="text-[10px] font-bold tracking-[0.3em] text-[#71B7F0] uppercase hover:underline no-print">
              ← Back to admin
            </Link>
            <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight mt-4">
              Site map
              <span className="block text-lg sm:text-xl font-normal text-slate-400 mt-3">
                Every route, every role — all in one place.
              </span>
            </h1>
          </div>
          <button
            onClick={handleDownload}
            className="no-print inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#070B16] font-semibold text-sm hover:bg-slate-100 transition shadow-lg shadow-black/40"
            data-testid="sitemap-download-pdf"
          >
            <FileDown className="w-4 h-4" /> Download PDF
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-8 inline-flex p-1 bg-white/5 rounded-full ring-1 ring-white/10 no-print" data-testid="sitemap-view-toggle">
          <button
            onClick={() => setView('map')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
              view === 'map' ? 'bg-white text-[#070B16]' : 'text-slate-300 hover:text-white'
            }`}
            data-testid="sitemap-view-pages"
          >
            <Map className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Pages
          </button>
          <button
            onClick={() => setView('api')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
              view === 'api' ? 'bg-white text-[#070B16]' : 'text-slate-300 hover:text-white'
            }`}
            data-testid="sitemap-view-api"
          >
            <Wrench className="w-4 h-4 inline mr-1.5 -mt-0.5" /> API endpoints
          </button>
        </div>
      </header>

      {/* Legend */}
      {view === 'map' && (
        <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 mb-10">
          <div className="flex flex-wrap items-center gap-3">
            {SECTIONS.map((s) => {
              const tint = ROLE_TINT[s.color];
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={`group flex items-center gap-2 px-3.5 py-2 rounded-full ${tint.soft} ring-1 ${tint.ring} hover:bg-white/10 transition`}
                >
                  <span className={`w-2 h-2 rounded-full ${tint.dot}`} />
                  <span className="text-xs font-semibold text-white">{s.title}</span>
                  <span className="text-[10px] font-mono text-slate-400">{s.routes.length}</span>
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Sections */}
      {view === 'map' && (
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-20 space-y-16">
          {SECTIONS.map((section, idx) => {
            const tint = ROLE_TINT[section.color];
            return (
              <section key={section.id} id={section.id} className="scroll-mt-12">
                <SectionHeader section={section} />

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {section.routes.map((r) => (
                    <RouteCard key={r.path} route={r} tint={tint} />
                  ))}
                </div>

                {section.features && (
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Features</span>
                    {section.features.map((f) => (
                      <span
                        key={f}
                        className={`text-xs px-2.5 py-1 rounded-full bg-white/[0.04] text-slate-300 ring-1 ring-white/10`}
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                )}

                {idx < SECTIONS.length - 1 && (
                  <div className="flex justify-center mt-12">
                    <ArrowDownRight className="w-5 h-5 text-slate-700" />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {view === 'api' && (
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-20 space-y-10">
          {Object.entries(ROLE_API).map(([key, eps]) => {
            const tint = ROLE_TINT[key] || ROLE_TINT.api;
            return (
              <section key={key}>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-3 h-3 rounded-full ${tint.solid}`} />
                  <h2 className="font-display text-xl text-white font-bold uppercase tracking-wide">{key}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {eps.map((ep) => (
                    <div
                      key={ep}
                      className="rounded-xl px-3.5 py-2.5 bg-white/[0.04] ring-1 ring-white/10 font-mono text-xs text-slate-300"
                    >
                      {ep}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <footer className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-12 pt-6 border-t border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <span>© Mohammed Riaz & Partner LLC · Muscat, Oman</span>
          <span>v1.2 · Auto-generated sitemap</span>
        </div>
      </footer>
    </main>
  );
};

export default SiteMap;
